import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';

const MAX_SHOWS_PER_NEWS = 3;
const INITIAL_DELAY_MS = 10000;
const RESTART_DELAY_MS = 10 * 60 * 1000;

@observer
class NewsTickerOverlay extends Component {
    constructor(props) {
        super(props);
        this.state = {
            newsList: [],
            currentIndex: 0,
            phase: 'waiting', // 'waiting', 'enter', 'visible', 'exit', 'done'
            isPlaying: false,
            showAllExpanded: false,
        };
        this.reloadTimer = null;
        this.phaseTimer = null;
        this.delayTimer = null;
        this.restartTimer = null;
        this.containerRef = null;
        this.videoEl = null;
        this.showCounts = {}; // { newsId: count } — tracks how many times each news has been shown
        this.wasDrawerOpen = false;
    }

    setContainerRef = (el) => {
        this.containerRef = el;
        if (!this.props.autoStart) {
            this.attachVideoListeners();
        }
    }

    attachVideoListeners = () => {
        if (!this.containerRef) return;
        const wrapper = this.containerRef.parentElement;
        if (!wrapper) return;
        const video = wrapper.querySelector('video');
        if (video && video !== this.videoEl) {
            if (this.videoEl) {
                this.videoEl.removeEventListener('play', this.onVideoPlay);
                this.videoEl.removeEventListener('pause', this.onVideoPause);
            }
            this.videoEl = video;
            video.addEventListener('play', this.onVideoPlay);
            video.addEventListener('pause', this.onVideoPause);
            if (!video.paused) {
                this.setState({ isPlaying: true }, () => {
                    this.scheduleStart();
                });
            }
        }
    }

    onVideoPlay = () => {
        this.setState({ isPlaying: true }, () => {
            this.scheduleStart();
        });
    }

    onVideoPause = () => {
        this.setState({ isPlaying: false });
        this.stopCycle();
        clearTimeout(this.delayTimer);
        this.delayTimer = null;
    }

    scheduleStart = () => {
        const { phase } = this.state;
        // If already cycling or waiting, don't restart
        if (this.delayTimer || (phase !== 'waiting' && phase !== 'done')) {
            // Resume cycling if we were mid-cycle when paused
            if (phase === 'enter' || phase === 'visible' || phase === 'exit') {
                this.startCycleFromEnter();
            }
            return;
        }
        if (phase === 'done') return;

        this.setState({ phase: 'waiting' });
        this.delayTimer = setTimeout(() => {
            this.delayTimer = null;
            const { isPlaying } = this.state;
            if (isPlaying) {
                this.advanceToNextAvailable();
            }
        }, INITIAL_DELAY_MS);
    }

    getNextAvailableIndex = (startIndex) => {
        const { newsList } = this.state;
        if (!newsList || newsList.length === 0) return -1;

        // Try each item starting from startIndex, wrapping around once
        for (let i = 0; i < newsList.length; i++) {
            const idx = (startIndex + i) % newsList.length;
            const newsId = newsList[idx].id;
            const count = this.showCounts[newsId] || 0;
            if (count < MAX_SHOWS_PER_NEWS) {
                return idx;
            }
        }
        return -1; // All items shown 3 times
    }

    advanceToNextAvailable = () => {
        const { currentIndex } = this.state;
        const nextIndex = this.getNextAvailableIndex(currentIndex);
        if (nextIndex === -1) {
            this.setState({ phase: 'done' });
            this.scheduleRestart();
            return;
        }
        // Record the show
        const { newsList } = this.state;
        const newsId = newsList[nextIndex].id;
        this.showCounts[newsId] = (this.showCounts[newsId] || 0) + 1;

        this.setState({ currentIndex: nextIndex, phase: 'enter' }, () => {
            this.startCycleFromEnter();
        });
    }

    startCycleFromEnter = () => {
        const { isPlaying } = this.state;
        if (!isPlaying) return;
        this.stopCycle();
        // In 'enter' phase, schedule visible → exit → next
        this.phaseTimer = setTimeout(() => {
            this.setState({ phase: 'visible' }, () => {
                this.phaseTimer = setTimeout(() => {
                    this.setState({ phase: 'exit' }, () => {
                        this.phaseTimer = setTimeout(() => {
                            // Advance to next available item
                            const { currentIndex, newsList } = this.state;
                            const nextStart = (currentIndex + 1) % newsList.length;
                            const nextIndex = this.getNextAvailableIndex(nextStart);
                            if (nextIndex === -1) {
                                this.setState({ phase: 'done' });
                                this.scheduleRestart();
                                return;
                            }
                            const newsId = newsList[nextIndex].id;
                            this.showCounts[newsId] = (this.showCounts[newsId] || 0) + 1;
                            this.setState({ currentIndex: nextIndex, phase: 'enter' }, () => {
                                this.startCycleFromEnter();
                            });
                        }, 500);
                    });
                }, 7000);
            });
        }, 500);
    }

    stopCycle = () => {
        clearTimeout(this.phaseTimer);
        this.phaseTimer = null;
    }

    scheduleRestart = () => {
        clearTimeout(this.restartTimer);
        this.restartTimer = setTimeout(() => {
            this.restartTimer = null;
            this.showCounts = {};
            const { isPlaying } = this.state;
            if (isPlaying) {
                this.setState({ phase: 'waiting', currentIndex: 0 }, () => {
                    this.scheduleStart();
                });
            }
        }, RESTART_DELAY_MS);
    }

    loadNews = async () => {
        const { raceId } = this.props;
        if (!raceId) return;

        const { newsStore } = this.props.stores;
        const localList = await newsStore.load({
            query: { race: raceId },
            url: '/api/news/public/',
            limit: 10,
            skipUpdate: true,
        });
        if (localList && localList.length > 0) {
            this.setState({ newsList: localList });
        } else {
            this.setState({ newsList: [] });
        }

        clearTimeout(this.reloadTimer);
        this.reloadTimer = setTimeout(() => {
            this.loadNews();
        }, 1000 * 60);
    }

    viewNews = (e) => {
        const { id, tag } = e.target.closest('.news-ticker-item').dataset;
        const { appState } = this.props.stores;
        const { drawerLevel = 0 } = this.props;
        const { drawerHeightMediumLarge } = appState;
        appState.openDrawer('viewNews', {
            id,
            tag,
            height: drawerHeightMediumLarge,
        }, drawerLevel + 1);
    }

    viewNewsFromList = (newsItem) => {
        const { appState } = this.props.stores;
        const { drawerLevel = 0 } = this.props;
        const { drawerHeightMediumLarge } = appState;
        appState.openDrawer('viewNews', {
            id: newsItem.id,
            tag: newsItem.tag || 'frontpage',
            height: drawerHeightMediumLarge,
        }, drawerLevel + 1);
    }

    toggleShowAll = () => {
        this.setState(prevState => ({ showAllExpanded: !prevState.showAllExpanded }));
    }

    componentDidMount() {
        this.loadNews();
        if (this.props.autoStart) {
            this.setState({ isPlaying: true }, () => {
                this.scheduleStart();
            });
        } else {
            this.retryTimer = setTimeout(() => {
                this.attachVideoListeners();
            }, 1000);
        }
    }

    componentWillUnmount() {
        clearTimeout(this.reloadTimer);
        clearTimeout(this.retryTimer);
        clearTimeout(this.delayTimer);
        clearTimeout(this.restartTimer);
        this.stopCycle();
        if (this.videoEl) {
            this.videoEl.removeEventListener('play', this.onVideoPlay);
            this.videoEl.removeEventListener('pause', this.onVideoPause);
            this.videoEl = null;
        }
    }

    isDrawerOpen = () => {
        const { appState } = this.props.stores;
        return appState.showDrawer1 || appState.showDrawer2 || appState.showDrawer3;
    }

    componentDidUpdate() {
        if (!this.props.autoStart) {
            this.attachVideoListeners();
        }
        const drawerOpen = this.isDrawerOpen();
        if (drawerOpen && !this.wasDrawerOpen) {
            // Drawer just opened — pause
            this.wasDrawerOpen = true;
            this.stopCycle();
            clearTimeout(this.delayTimer);
            this.delayTimer = null;
        } else if (!drawerOpen && this.wasDrawerOpen) {
            // Drawer just closed — resume
            this.wasDrawerOpen = false;
            const { isPlaying, phase } = this.state;
            if (isPlaying && phase !== 'done') {
                this.scheduleStart();
            }
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.raceId !== this.props.raceId) {
            this.loadNews();
        }
    }

    render() {
        const { newsList, currentIndex, phase, isPlaying, showAllExpanded } = this.state;

        const drawerOpen = this.isDrawerOpen();
        const hasNews = newsList && newsList.length > 0;
        const tickerActive = hasNews && isPlaying && !drawerOpen
            && phase !== 'waiting' && phase !== 'done';

        // Nothing to show at all
        if (!hasNews) {
            return <div ref={this.setContainerRef} />;
        }

        const news = tickerActive ? newsList[currentIndex] : null;
        const phaseClass = phase === 'enter' ? 'news-ticker-item-enter'
            : phase === 'exit' ? 'news-ticker-item-exit'
            : 'news-ticker-item-visible';

        const { maxWidth } = this.props;

        return (
            <div
                ref={this.setContainerRef}
                class={`position-absolute ${maxWidth ? '' : 'w-100'}`}
                style={`bottom: ${this.props.bottom || '70px'}; left: 0; z-index: 1000; pointer-events: none;${maxWidth ? ` max-width: ${maxWidth};` : ''}`}
            >
                <div class='d-flex flex-column align-items-start'>
                    {showAllExpanded && (
                        <div
                            class='news-expanded-list mb-1 ms-0'
                            style='max-height: 400px; overflow-y: auto; pointer-events: auto; background: rgba(0,0,0,0.7); border-radius: 4px; min-width: 300px; max-width: 500px;'
                        >
                            {newsList.map((item) => {
                                const timeSince = util.formatDistance(item.publishDate || item.date, new Date(), { textAgo: 'siden' });
                                return (
                                    <div
                                        key={item.id}
                                        class='news-list-item d-flex align-items-center py-2 px-3'
                                        style='cursor: pointer; border-bottom: 1px solid rgba(255,255,255,0.1);'
                                        onClick={() => this.viewNewsFromList(item)}
                                    >
                                        <i class='fa-solid fa-circle text-danger me-2 flex-shrink-0' style='font-size: 6px;' />
                                        <span class='text-white text-truncate' style='font-size: 0.8rem;'>
                                            {item.title}
                                        </span>
                                        <span class='text-white-50 ms-2 flex-shrink-0' style='font-size: 0.7rem;'>
                                            {timeSince}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div class='d-flex align-items-center' style={maxWidth ? 'overflow: hidden;' : ''}>
                        <div
                            class='d-inline-flex align-items-center py-1 px-3 flex-shrink-0'
                            style='background: rgba(0,0,0,0.7); pointer-events: auto; cursor: pointer; white-space: nowrap; border-radius: 0 4px 4px 0;'
                            onClick={this.toggleShowAll}
                        >
                            {showAllExpanded ? (
                                <span class='text-white' style='font-size: 0.8rem;'>
                                    <i class='fa-solid fa-chevron-down me-1' style='font-size: 0.6rem;' />
                                    Skjul
                                </span>
                            ) : (
                                <span class='text-white' style='font-size: 0.8rem;'>
                                    <i class='fa-solid fa-chevron-up me-1' style='font-size: 0.6rem;' />
                                    Nyheter ({newsList.length})
                                </span>
                            )}
                        </div>

                        {tickerActive && news && (
                            <div
                                key={`${news.id}-${currentIndex}-${this.showCounts[news.id]}`}
                                class={`d-inline-flex align-items-center py-1 px-3 ms-1 news-ticker-item ${phaseClass}`}
                                style='background: rgba(0,0,0,0.7); pointer-events: auto; cursor: pointer; white-space: nowrap;'
                                data-id={news.id}
                                data-tag={news.tag || 'frontpage'}
                                onClick={this.viewNews}
                            >
                                <i class='fa-solid fa-circle text-danger me-2' style='font-size: 6px;' />
                                <span class='text-white' style='font-size: 0.8rem;'>
                                    {news.title}
                                </span>
                                <span class='text-white-50 ms-2' style='font-size: 0.7rem;'>
                                    {util.formatDistance(news.publishDate || news.date, new Date(), { textAgo: 'siden' })}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default NewsTickerOverlay;
